import { useQuery } from '@tanstack/react-query';
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat';
import MessagesView from '../views/MessagesView';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';
import { initials } from '../lib/dates';
import { disablePush, enablePush, getPushState, PushState } from '../api/push';
import { getWebauthnStatus, registerPasskey, webauthnSupported } from '../api/webauthn';
import TodayView from '../views/TodayView';
import PlanningView from '../views/PlanningView';
import StudentsView from '../views/StudentsView';
import ForecastsView from '../views/ForecastsView';
import SupportView from '../views/SupportView';
import AbsencesView from '../views/AbsencesView';
import InvoicesView from '../views/InvoicesView';
import VehiclesView from '../views/VehiclesView';
import { DamageReportSheet } from './vehicle/DamageReportSheet';
import { useVehicleDamagesEnabled } from '../api/vehicleDamages';
import { instructorApi } from '../api/instructor';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import BilanPage from '../views/bilan/BilanPage';
import EvaluationPage from '../views/evaluation/EvaluationPage';
import LessonsSheet from './LessonsSheet';
import InstallAppBanner from './InstallAppBanner';
import UpdateAppBanner from './UpdateAppBanner';
import { ProfileDialog } from './Photos';
import { NAV, type NavItem } from './shell/nav';
import { BottomBar, SideNav } from './shell/Navigation';
import { AccountMenu, NotificationsMenu } from './shell/HeaderMenus';

// Tableau (plein écran) : chargé à sa première ouverture seulement.
const BoardView = lazy(() => import('../views/board/BoardView'));

/**
 * Cadre de l'application moniteur (refonte du 14/10/2026) : colonne de navigation à gauche sur tablette et
 * ordinateur, barre du bas sur téléphone ; en-tête avec l'auto-école, les notifications et le compte.
 */
export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const section = useUiStore((s) => s.section);
  const setSection = useUiStore((s) => s.setSection);
  const unread = useUnreadMessages(section === 'messages');
  usePresenceHeartbeat(!!user);
  const [pushState, setPushState] = useState<PushState>('default');
  const [pushBusy, setPushBusy] = useState(false);
  const [bioRegistered, setBioRegistered] = useState<boolean | null>(null);
  const [bioBusy, setBioBusy] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // État de la connexion biométrique, lu à la première ouverture du menu du compte.
  const readBio = useCallback(() => {
    if (!webauthnSupported() || bioRegistered !== null) return;
    getWebauthnStatus()
      .then(setBioRegistered)
      .catch(() => setBioRegistered(false));
  }, [bioRegistered]);

  const enrollBiometric = async () => {
    setBioBusy(true);
    try {
      await registerPasskey();
      setBioRegistered(true);
    } catch {
      // silencieux : l'utilisateur a annulé ou l'appareil ne supporte pas.
    } finally {
      setBioBusy(false);
    }
  };

  // Au montage, permission déjà accordée : l'abonnement de cet appareil est renvoyé au serveur pour le compte
  // connecté (même déjà abonné : le serveur a pu le perdre, ou le garder au nom d'un autre compte, J29).
  useEffect(() => {
    let cancelled = false;
    getPushState()
      .then(async (st) => {
        if (cancelled) return;
        if (st !== 'unsupported' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            const next = await enablePush();
            if (!cancelled) setPushState(next);
            return;
          } catch {
            // Le bouton « Activer les notifications » reste proposé.
            if (!cancelled) setPushState('default');
            return;
          }
        }
        setPushState(st);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const [pushError, setPushError] = useState<string | null>(null);
  const togglePush = async () => {
    setPushBusy(true);
    setPushError(null);
    try {
      setPushState(pushState === 'subscribed' ? await disablePush() : await enablePush());
    } catch (error) {
      setPushError(
        error instanceof Error && error.message === 'Notifications non configurées côté serveur.'
          ? error.message
          : 'Activation impossible pour l’instant. Réessayez.',
      );
    } finally {
      setPushBusy(false);
    }
  };

  // Déconnexion : l'appareil cesse de recevoir les notifications de ce compte.
  const signOut = async () => {
    if (pushState === 'subscribed') await disablePush().catch(() => undefined);
    await logout();
  };

  // Pages de travail (livret et résumé de la séance, évaluation) : affichées à la place de la rubrique.
  const bilanTarget = useUiStore((s) => s.bilanTarget);
  const evalStudentId = useUiStore((s) => s.evalStudentId);
  const page = bilanTarget ? 'bilan' : evalStudentId ? 'evaluation' : null;

  // Changement de rubrique : retour en haut. Page ouverte : en haut ; refermée : retour à la hauteur d'avant.
  const scrolls = useRef<Record<string, number>>({});
  const shown = page ?? section;
  const previous = useRef(shown);
  useEffect(() => {
    scrolls.current[previous.current] = window.scrollY;
    const back = page === null && ['bilan', 'evaluation'].includes(previous.current);
    window.scrollTo({ top: back ? scrolls.current[section] ?? 0 : 0 });
    previous.current = shown;
  }, [shown, page, section]);

  // « Signaler un problème » : retour vers la rubrique d'où vient le moniteur.
  const lastSection = useRef<NavItem>(NAV[0]);
  useEffect(() => {
    if (section !== 'support') lastSection.current = NAV.find((n) => n.id === section) ?? NAV[0];
  }, [section]);

  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Moniteur';
  // « Tableau » ne change pas de rubrique : il s'ouvre en plein écran par-dessus, et « Quitter » ramène ici.
  const boardOpen = useUiStore((s) => s.boardOpen);
  const openBoard = useUiStore((s) => s.openBoard);
  const closeBoard = useUiStore((s) => s.closeBoard);
  const damageReport = useUiStore((s) => s.damageReport);
  const closeDamageReport = useUiStore((s) => s.closeDamageReport);
  const onSection = (s: NavItem['id']) => (s === 'tableau' ? openBoard() : setSection(s));
  // « Mes factures » : tout moniteur à la fiche active (indépendant ou salarié), ou qui a déjà envoyé des factures.
  const invoices = useQuery({ queryKey: ['my-invoices'], queryFn: instructorApi.getInvoices, staleTime: 5 * 60_000 });
  // Un salarié y envoie ses notes de frais : la rubrique porte ce nom.
  // « Véhicules » : seulement si l'école gère ses véhicules dans Permio.
  const vehiclesEnabled = useVehicleDamagesEnabled();
  const items = NAV.filter(
    (n) => (n.id !== 'factures' || invoices.data?.enabled === true || !!invoices.data?.items?.length) && (n.id !== 'vehicules' || vehiclesEnabled),
  ).map((n) =>
    n.id === 'factures' && invoices.data?.kind === 'expense' ? { ...n, label: 'Frais', title: 'Mes notes de frais' } : n,
  );
  const nav = { items, section, onSection, unreadMessages: unread };

  return (
    <div className="min-h-screen bg-surface-light">
      <SideNav {...nav} />
      <div className="md:pl-[88px] xl:pl-[232px]">
        <header className="sticky top-0 z-30 border-b border-line bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur">
          <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
            <img src="/logo-permio-moniteur.png" alt="Permio" className="h-7 w-auto shrink-0 md:hidden" />
            {user?.schoolName && (
              <span className="hidden min-w-0 truncate text-[13px] font-medium text-secondary sm:block">
                <span className="mr-3 inline-block h-5 w-px bg-line align-middle md:hidden" aria-hidden />
                {user.schoolName}
              </span>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              <NotificationsMenu pushState={pushState} pushBusy={pushBusy} pushError={pushError} onTogglePush={togglePush} />
              <AccountMenu
                userId={user?.id}
                fullName={name}
                email={user?.email}
                monogram={initials(user?.firstName ?? 'M', user?.lastName ?? 'O')}
                bioSupported={webauthnSupported()}
                bioRegistered={bioRegistered}
                bioBusy={bioBusy}
                onOpen={readBio}
                onEnableBio={enrollBiometric}
                onProfile={() => setProfileOpen(true)}
                onSupport={() => setSection('support')}
                onLogout={() => void signOut()}
              />
            </div>
          </div>
        </header>

        <UpdateAppBanner />
        <InstallAppBanner />

        <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 md:pb-10 md:pt-8">
          {page === 'bilan' && <BilanPage />}
          {page === 'evaluation' && <EvaluationPage />}
          {/* La rubrique reste montée sous la page (filtres, recherche gardés), simplement masquée. */}
          <div hidden={!!page}>
            {section === 'jour' && <TodayView />}
            {section === 'planning' && <PlanningView />}
            {section === 'eleves' && <StudentsView />}
            {section === 'previsions' && <ForecastsView />}
            {section === 'absences' && <AbsencesView />}
            {section === 'factures' && <InvoicesView />}
            {section === 'vehicules' && <VehiclesView />}
            {section === 'messages' && <MessagesView />}
            {section === 'support' && (
              <SupportView onDone={() => setSection(lastSection.current.id)} doneLabel={`Retour : ${lastSection.current.title}`} />
            )}
          </div>
        </main>
      </div>
      <BottomBar {...nav} />

      <LessonsSheet />
      {boardOpen && (
        <Suspense fallback={<div className="fixed inset-0 z-[60] bg-white" aria-busy="true" />}>
          <BoardView onClose={closeBoard} />
        </Suspense>
      )}
      {profileOpen && <ProfileDialog onClose={() => setProfileOpen(false)} />}
      {damageReport && <DamageReportSheet vehicleId={damageReport.vehicleId} onClose={closeDamageReport} />}
    </div>
  );
}
